import { graphql, useStaticQuery } from "gatsby";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import Select from "react-select";

import Image from "../../../components/image";
import CtaButton from "../../../components/shared/buttons/ctaButton";
import { useTheme } from "../../../context/ThemeContext";
import ArrowRightBigIcon from "../../../images/icons/arrow-right-big-icon.inline.svg";
import ArrowRight from "../../../images/icons/arrow-right.inline.svg";
import LocationIcon from "../../../images/icons/location-icon.inline.svg";
import { getCDNProviderToUse } from "../../../utils/base";
import { Link } from "../../../utils/customLink";
import ProgramSlides from "./ProgramSlides";

const formatOptionLabel = ({ value, label }) => (
  <div className="flex">
    <div>{label}</div>
  </div>
);

const PlaceholderFC = ({ prefix, body }) => (
  <button className={`flex justify-between items-center grow`}>
    <div className="items-center text-left">
      <p className="h8 opacity-60">{prefix}</p>
      <h6 className="font-bold text-black-lighter">{body}</h6>
    </div>
  </button>
);

const CountryWithPrograms = ({ pageRef }) => {
  const { genericDetails } = useStaticQuery(graphql`
    query {
      genericDetails: wp {
        globalProgramConfig: gviConfigSettings {
          acfGenericGlobalConfig {
            ...FeaturedLocationsFragment
          }
        }
      }
    }
  `);

  const { colorMode, peopleDomain } = useTheme();
  const [, render] = useReducer((p) => !p, false);
  const [locationList, setLocationList] = useState([]);

  const stickyRef = useRef(null);
  const navPinRef = useRef(null);

  const currentSlide = useRef(1);
  const changable = useRef(true);

  const [relatedPrograms, setRelatedPrograms] = useState();
  const [exploreProgramsCTA, setExploreProgramsCTA] = useState({});

  const customStyles = {
    menu: (provided, state) => ({
      //listbox
      ...provided,
      borderRadius: 0,
      padding: 0,
      margin: 0,
      background: colorMode === "light" ? provided.background : "white",
      border: "1px solid rgba(231, 231, 242, 0.3)",
      boxShadow: 0,
      backdropFilter: "blur(50px)"
    }),
    menuList: (provided, state) => ({
      ...provided,
      background: colorMode === "light" ? "white" : "transparent",
      padding: 0
    }),
    option: (provided, state) => ({
      ...provided,
      zIndex: 20,
      color: colorMode === "light" ? "#4D5A62" : "white",
      background: colorMode === "light" ? "white" : "transparent",
      fontWeight: state.isSelected ? 700 : 400,
      height: "100%"
    }),
    control: (provided, state) => ({
      ...provided,
      background: colorMode === "light" ? "white" : "transparent",
      padding: "4px",
      height: "100%",
      border: 0,
      borderRadius: 0
    }),
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";
      const color = colorMode === "light" ? "#4D5A62" : "white";
      const fontSize = "16px";
      const fontWeight = "bold";

      return {
        ...provided,
        opacity,
        transition,
        color,
        fontSize,
        fontWeight
      };
    },
    placeholder: (defaultStyles) => {
      return {
        ...defaultStyles,
        color: "#ffffff"
      };
    },
    indicatorSeparator: () => null
  };

  const secondaryProgramList = useMemo(() => {
    return (
      []
        ?.map((prog) => {
          return {
            title: prog.title,
            featuredImage: prog.featuredImage,
            uri: [peopleDomain, prog.uri].join("")
          };
        })
        ?.filter((item) => !/-test/gi.test(item.uri)) ?? []
    );
  }, [peopleDomain]);

  const sections = useMemo(() => {
    return (
      genericDetails?.globalProgramConfig?.acfGenericGlobalConfig?.featuredLocations?.map(
        (featuredLocation) => {
          const programList =
            featuredLocation?.countryGeographical
              ?.flatMap((programList) => {
                return programList?.programs?.nodes?.map((program) => {
                  return {
                    title: program.title,
                    featuredImage: program.featuredImage,
                    uri: program.uri,
                    programType: program.acfProgramsMeta?.programTypes
                  };
                });
              })
              ?.filter((item) => !/-test/gi.test(item.uri)) ?? [];

          const programType =
            featuredLocation?.countryGeographical?.flatMap((programList) => {
              return programList?.programs?.nodes?.flatMap((program) => {
                return program?.acfProgramsMeta?.programTypes?.flatMap(
                  (program) => {
                    return {
                      title: program.title
                    };
                  }
                );
              });
            }) ?? [];

          const formattedLinks =
            featuredLocation?.countryGeographical?.flatMap((location) => {
              const titleToUse =
                featuredLocation?.locationSubtitle ?? location?.name;
              return `<a href="${location?.geographicalTaxonomy?.pageLink?.uri ?? "#"}" title="${titleToUse}" class="underline">${titleToUse}</a>`;
            }) || [];

          const locationLinks = formattedLinks?.join(" and ") || "";

          const responseObj = {
            areaName: featuredLocation.locationSubtitle
              ? formattedLinks[0] || featuredLocation.locationSubtitle || ""
              : locationLinks || "",
            areaName_alt: locationLinks ?? featuredLocation.locationSubtitle,
            displayName: featuredLocation.locationMainTitle,
            background: {
              gatsbyImage:
                featuredLocation.locationBackgroundImage?.gatsbyImage,
              localFile: featuredLocation.locationBackgroundImage?.localFile,
              sourceUrl: featuredLocation.locationBackgroundImage?.sourceUrl
            },
            description: featuredLocation.locationSummary,
            programs: programList,
            ctaLink: featuredLocation.locationButtonLink ?? "#",
            ctaTitle:
              featuredLocation.locationCtaTitle ?? "Explore all programs",
            programType: programType
          };

          // console.debug("featuredLocation", featuredLocation);
          if (featuredLocation.peoplePrograms === true) {
            return {
              ...responseObj,
              programs: secondaryProgramList,
              ctaLink: peopleDomain
            };
          } else {
            return responseObj;
          }
        }
      ) ?? []
    );
  }, [
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig
      ?.featuredLocations,
    peopleDomain,
    secondaryProgramList
  ]);

  useEffect(() => {
    if (sections?.length > 0) {
      setRelatedPrograms(sections[0].programs);
      setExploreProgramsCTA({
        ctaLink: sections[0].ctaLink,
        ctaTitle: sections[0].ctaTitle
      });
    }
    setLocationList(
      sections.map((loc, idx) => ({
        value: loc.displayName,
        label: loc.displayName,
        areaName: loc.areaName,
        locIdx: idx
      }))
    );
  }, []);

  useEffect(() => {
    if (sections?.length > 0) {
      setRelatedPrograms(sections[currentSlide.current - 1].programs);
      setExploreProgramsCTA({
        ctaLink: sections[currentSlide.current - 1].ctaLink,
        ctaTitle: sections[currentSlide.current - 1].ctaTitle
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide.current]);

  /* Update DOM & slide index */
  const scrollDownAction = () => {
    const containerElement = document.getElementsByClassName("stickyContainer");
    if (
      containerElement.length > 0 &&
      changable.current &&
      currentSlide.current <= sections.length
    ) {
      changable.current = false;
      document
        .querySelector(".single-slide:nth-child(" + currentSlide.current + ")")
        .classList.remove("active");
      if (currentSlide.current === sections.length) currentSlide.current = 1;
      else currentSlide.current++;
      render();
      document
        .querySelector(".single-slide:nth-child(" + currentSlide.current + ")")
        .classList.add("active");
      setTimeout(() => {
        changable.current = true;
      }, 1000);
    }
  };

  /* Update DOM & slide index */
  const scrollUpAction = () => {
    const containerElement = document.getElementsByClassName("stickyContainer");
    if (
      containerElement.length > 0 &&
      changable.current &&
      currentSlide.current >= 1
    ) {
      changable.current = false;
      document
        .querySelector(".single-slide:nth-child(" + currentSlide.current + ")")
        .classList.remove("active");

      if (currentSlide.current == 1) currentSlide.current = sections.length;
      else currentSlide.current--;
      render();
      document
        .querySelector(".single-slide:nth-child(" + currentSlide.current + ")")
        .classList.add("active");
      setTimeout(() => {
        changable.current = true;
      }, 1000);
    }
  };

  const handleChange = useCallback(
    (value) => {
      setRelatedPrograms(sections[value.locIdx].programs);
      setExploreProgramsCTA({
        ctaLink: sections[value.locIdx].ctaLink,
        ctaTitle: sections[value.locIdx].ctaTitle
      });
    },
    [sections]
  );

  return (
    <section
      className="relative country-with-programs-section md:min-h-[600px] px-12 py-6 min-h-[650px]"
      ref={stickyRef}
    >
      {sections.map((country, idx) => (
        <div
          className={`absolute top-0 left-0 w-full h-full single-slide ${
            idx === 0 && sections.length > 1 ? "active" : ""
          }`}
          key={idx}
        >
          <div
            className={`absolute z-[-1] top-0 left-0 w-full h-full ${
              sections.length > 1 ? "opacity-0" : ""
            } location-bg gvi-ease-in-out`}
            style={{
              backgroundImage:
                "linear-gradient(rgba(0,0,0,0.37),rgba(0,0,0,0.37))"
            }}
          />
          <Image
            imageClass={`absolute z-[-2] top-0 left-0 w-full h-full ${
              sections.length > 1 ? "opacity-0" : ""
            } location-bg gvi-ease-in-out object-cover object-cover `}
            sourceUrl={getCDNProviderToUse({
              width: 1200,
              uri: country?.background?.sourceUrl
            })}
            image={country?.background?.gatsbyImage}
          />
          <div
            className={`location-content hidden md:block absolute top-1/2 translate-y-[-50%] !left-[96px] max-w-[480px] text-white ${
              sections.length > 1 ? "opacity-0" : ""
            } gvi-ease-in-out`}
          >
            <div className="flex items-center">
              <LocationIcon className="w-5 mr-2 fill-white" />
              <p className="h7 uppercase !text-white">Programs in</p>
            </div>
            <h1
              className="text-white font-[300]"
              dangerouslySetInnerHTML={{ __html: country.displayName }}
            />
            <div
              className="text-xl text-white font-[300] leading-[155.5%] pb-2"
              dangerouslySetInnerHTML={{ __html: country.areaName }}
            />
            <div
              className="planet:text-gray-septenary text-base"
              dangerouslySetInnerHTML={{ __html: country.description }}
            />
            <Link
              className="flex gap-2 items-center w-full md:w-max justify-center h-10 text-base text-white planet:text-sm font-semibold leading-7 tracking-wider bg-transparent mt-2 px-7 rounded-xxxxl planet:rounded-sm planet:border-white border hover:text-white"
              to={country.ctaLink}
            >
              <span>{country.ctaTitle}</span>
              <ArrowRightBigIcon className="block fill-white planet:hidden" />
              <ArrowRight className="hidden planet:block stroke-white" />
            </Link>
          </div>
        </div>
      ))}
      <div className="slider-section absolute right-0 md:top-1/2 top-[54%] h-[320px] w-screen md:w-[50vw] z-20 text-white -translate-y-1/2 md:px-0 px-4">
        <div className="md:hidden block absolute -top-[120px] left-0 px-4">
          <p className="text-[19px] font-bold leading-[23px] planet:text-white mb-2">
            Select a location you’re interested in to see our programs
          </p>
          <div className="h-full grow shrink basis-0">
            <Select
              formatOptionLabel={formatOptionLabel}
              options={locationList}
              className="z-20 h-full bg-white rounded-none location-selectbox planet:gvi-blur-bg planet:border planet:border-gray-testimonial/30 planet:sm:border-0 planet:sm:bg-transparent"
              styles={customStyles}
              onChange={handleChange}
              placeholder={
                <PlaceholderFC prefix={"Choose a"} body={"Location"} />
              }
            />
          </div>
        </div>
        {relatedPrograms && relatedPrograms.length > 0 && (
          <ProgramSlides
            relatedPrograms={relatedPrograms}
            key={"program-slides-key"}
          />
        )}
        {exploreProgramsCTA?.ctaLink && exploreProgramsCTA?.ctaTitle && (
          <div className="absolute z-10 md:-bottom-[110px] -bottom-[76px] px-4 left-0 flex justify-center w-full md:hidden">
            <CtaButton
              preset={colorMode === "light" ? "tertiary" : "secondary"}
              labelClass="text-base leading-genericCta font-bold pr-2"
              extraButtonStyle={"w-full"}
              to={exploreProgramsCTA?.ctaLink}
              ctaLabel={exploreProgramsCTA?.ctaTitle}
              arrowSize={"sml"}
            />
          </div>
        )}
      </div>

      <div className="absolute top-0 z-10 hidden w-1 h-full nav-scroll left-8 md:block">
        <div className="relative flex flex-col items-center justify-center w-full h-full nav-scroll-wrapper">
          <button
            className={`absolute flex items-center justify-center left-0 w-[46px] h-[46px] bg-white/20 backdrop-blur-lg -ml-3 cursor-pointer up-nav top-6 border-[2px] border-white`}
            onClick={() => scrollUpAction()}
          >
            <ArrowRight className="stroke-white w-[15px] h-[15px] -rotate-90" />
          </button>
          <div className="nav-stick w-full h-[70%] absolute left-[18px] bg-white bg-opacity-20 -ml-2 z-20">
            <div className="z-10 centering" ref={navPinRef}>
              <span className="absolute -left-[23px] -top-[10px] w-[46px] h-[46px] bg-white bg-opacity-20 rounded-full z-10" />
              <span className="absolute -left-[13px] top-0 w-[26px] h-[26px] bg-white rounded-full z-20" />
            </div>
          </div>
          <button
            className={`absolute flex items-center justify-center left-0 w-[46px] h-[46px] bg-white/20 backdrop-blur-lg -ml-3 cursor-pointer down-nav bottom-6 border-[2px] border-white`}
            onClick={() => scrollDownAction()}
          >
            <ArrowRight className="stroke-white w-[15px] h-[15px] rotate-90" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CountryWithPrograms;
